import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getClicksPagesOverview } from '../api/feed';
import { ClicksPagesOverviewResponse, ClicksPageOverviewRow } from '../api/types';
import PageHeader from '../components/shared/PageHeader';
import SubTabs from '../components/shared/SubTabs';
import { SkelRow } from '../components/shared/Skeleton';

const TRAFFIC_TABS = [
  { label: 'Overview', to: '/traffic',         end: true },
  { label: 'By page',  to: '/traffic/by-page'            }
];

const RANGES = [
  { value: 1,  label: '1d' },
  { value: 3,  label: '3d' },
  { value: 7,  label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' }
] as const;

const DOMAINS = [
  { value: '',     label: 'all domains' },
  { value: 'main', label: 'main' },
  { value: 'osr',  label: 'osr' },
  { value: 'o2s',  label: 'o2s' },
  { value: 'sf',   label: 'sf' },
  { value: 'fs',   label: 'fs' }
] as const;

const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat('en-US').format(n) : '—';
const fmtPct = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';

type SortField = 'pageKey' | 'total' | 'confirmed' | 'ctr' | 'bounceRate' | 'modelsTouched';
type SortDir = 'asc' | 'desc';

const ClicksByPageOverviewPage: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [domain, setDomain] = useState<string>('');
  const [query, setQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('total');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading, isError } = useQuery<ClicksPagesOverviewResponse>({
    queryKey: ['clicks-pages-overview', days, domain],
    queryFn: () => getClicksPagesOverview(days, {
      domain: domain || undefined,
      // Default Tier 1+2 + PPC — паритет с провайдером.
      // TODO(UI): add selectors mirror'ing TrafficPage если понадобится override.
      monetization: 'PPC',
      tiers: 'tier1,tier2'
    })
  });

  const filteredSorted = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const filtered = q ? data.pages.filter(p => p.pageKey.toLowerCase().includes(q)) : data.pages;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = pickSortValue(a, sortField);
      const bv = pickSortValue(b, sortField);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [data, query, sortField, sortDir]);

  // Aggregated KPIs across filtered rows.
  const summary = useMemo(() => {
    const acc = { total: 0, confirmed: 0, bounced: 0, bot: 0, dup: 0, pages: 0 };
    for (const r of filteredSorted) {
      acc.total += r.total;
      acc.confirmed += r.confirmed;
      acc.bounced += r.bounced;
      acc.bot += r.bot;
      acc.dup += r.duplicate;
      acc.pages += 1;
    }
    return {
      ...acc,
      ctr: acc.total > 0 ? acc.confirmed / acc.total : null,
      bounceRate: acc.total > 0 ? acc.bounced / acc.total : null
    };
  }, [filteredSorted]);

  function toggleSort(f: SortField) {
    if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('desc'); }
  }
  const sortIndicator = (f: SortField) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const exportCsv = () => {
    const header = ['page_key', 'clicks', 'confirmed', 'bounced', 'bot', 'duplicate', 'models_touched', 'ctr', 'bounce_rate', 'last_click'];
    const lines: string[] = [header.join(',')];
    for (const r of filteredSorted) {
      lines.push([
        '"' + r.pageKey + '"', r.total, r.confirmed, r.bounced, r.bot, r.duplicate, r.modelsTouched,
        r.ctr != null ? r.ctr.toFixed(4) : '',
        r.bounceRate != null ? r.bounceRate.toFixed(4) : '',
        r.lastClickAt ?? ''
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clicks-by-page-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const actions = (
    <button className="hf-btn" onClick={exportCsv} disabled={!data || data.pages.length === 0}>
      <Download size={14} /> Export CSV
    </button>
  );

  return (
    <>
      <PageHeader
        title="Traffic"
        subtitle="Click funnel per pageKey · tracking v2"
        actions={actions}
      />

      <div className="hf-content">
        <SubTabs items={TRAFFIC_TABS} />
        <div className="hf-toolbar">
          <div className="hf-segmented">
            {RANGES.map(r => (
              <button key={r.value} className={days === r.value ? 'on' : ''} onClick={() => setDays(r.value)}>{r.label}</button>
            ))}
          </div>
          <div className="hf-segmented">
            {DOMAINS.map(d => (
              <button key={d.value} className={domain === d.value ? 'on' : ''} onClick={() => setDomain(d.value)}>{d.label}</button>
            ))}
          </div>
          <div className="hf-input" style={{ minWidth: 240, marginLeft: 'auto' }}>
            <Search size={14} />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="filter by pageKey" />
          </div>
        </div>

        {/* KPI strip — over filtered rows */}
        <div className="hf-kpi-strip">
          <div className="hf-kpi accent">
            <span className="hf-kpi-label">Pages</span>
            <span className="hf-kpi-value">{fmt(summary.pages)}</span>
            <span className="hf-kpi-sub">with traffic in last {days}d</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Clicks</span>
            <span className="hf-kpi-value">{fmt(summary.total)}</span>
            <span className="hf-kpi-sub">CTR {fmtPct(summary.ctr)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Confirmed</span>
            <span className="hf-kpi-value">{fmt(summary.confirmed)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Bounced</span>
            <span className="hf-kpi-value">{fmt(summary.bounced)}</span>
            <span className="hf-kpi-sub">{fmtPct(summary.bounceRate)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Bots / Dup</span>
            <span className="hf-kpi-value">{fmt(summary.bot + summary.dup)}</span>
            <span className="hf-kpi-sub">{fmt(summary.bot)} bot · {fmt(summary.dup)} dup</span>
          </div>
        </div>

        {isLoading && (
          <div className="hf-card flush">
            <table className="hf-table">
              <thead>
                <tr>
                  <th>pageKey</th>
                  <th className="num">clicks</th>
                  <th className="num">confirmed</th>
                  <th className="num">bounced</th>
                  <th className="num">CTR</th>
                  <th className="num">bounce%</th>
                  <th className="num">models</th>
                  <th className="num">bot</th>
                  <th className="num">dup</th>
                  <th>last click</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 10 }).map((_, i) => (
                  <SkelRow key={i} cols={11} numCols={[1, 2, 3, 4, 5, 6, 7, 8]} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isError && <div className="hf-banner danger">Failed to load page overview.</div>}

        {data && (
          <div className="hf-card flush">
            <table className="hf-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('pageKey')} style={{ cursor: 'pointer' }}>pageKey{sortIndicator('pageKey')}</th>
                  <th className="num" onClick={() => toggleSort('total')} style={{ cursor: 'pointer' }}>clicks{sortIndicator('total')}</th>
                  <th className="num" onClick={() => toggleSort('confirmed')} style={{ cursor: 'pointer' }}>confirmed{sortIndicator('confirmed')}</th>
                  <th className="num">bounced</th>
                  <th className="num" onClick={() => toggleSort('ctr')} style={{ cursor: 'pointer' }}>CTR{sortIndicator('ctr')}</th>
                  <th className="num" onClick={() => toggleSort('bounceRate')} style={{ cursor: 'pointer' }}>bounce%{sortIndicator('bounceRate')}</th>
                  <th className="num" onClick={() => toggleSort('modelsTouched')} style={{ cursor: 'pointer' }}>models{sortIndicator('modelsTouched')}</th>
                  <th className="num">bot</th>
                  <th className="num">dup</th>
                  <th>last click</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map(r => (
                  <tr key={r.pageKey}>
                    <td><span className="hf-mono">{r.pageKey}</span></td>
                    <td className="num" style={{ fontWeight: 600 }}>{fmt(r.total)}</td>
                    <td className="num">{fmt(r.confirmed)}</td>
                    <td className="num">{fmt(r.bounced)}</td>
                    <td className="num hf-mono">{fmtPct(r.ctr)}</td>
                    <td className="num hf-mono">{fmtPct(r.bounceRate)}</td>
                    <td className="num">{fmt(r.modelsTouched)}</td>
                    <td className="num" style={{ color: 'var(--fg-3)' }}>{fmt(r.bot)}</td>
                    <td className="num" style={{ color: 'var(--fg-3)' }}>{fmt(r.duplicate)}</td>
                    <td style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                      {r.lastClickAt ? new Date(r.lastClickAt).toISOString().slice(0, 16).replace('T', ' ') : '—'}
                    </td>
                    <td>
                      <Link
                        to={`/cpc?pageKey=${encodeURIComponent(r.pageKey)}`}
                        className="hf-btn ghost sm"
                        title="Open CPC drilldown for this page"
                      >
                        CPC <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

function pickSortValue(r: ClicksPageOverviewRow, f: SortField): string | number | null {
  switch (f) {
    case 'pageKey': return r.pageKey;
    case 'total': return r.total;
    case 'confirmed': return r.confirmed;
    case 'ctr': return r.ctr;
    case 'bounceRate': return r.bounceRate;
    case 'modelsTouched': return r.modelsTouched;
  }
}

export default ClicksByPageOverviewPage;
